// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * CardNode — Rectangular card-shaped node for SolyticsFlow.
 *
 * Generic shape-based node. Developers can style it directly with:
 *   data.color / data.bgColor / data.borderColor / data.textColor
 * and may use theme-token strings such as `primary.main`.
 *
 * `data.colorScheme` remains available as a convenience fallback.
 *
 * Supports:
 *   data.label       — display text
 *   data.colorScheme — theme colour preset key (default: 'default')
 *   data.tooltip     — string | ReactNode shown on hover
 *   data.menuItems   — SolyticsMenus item array for right-click menu
 *
 * CardShape is also exported for use by consumers who want just the visual
 * without ReactFlow handles or context menus (e.g. sidebar previews).
 */
import React, { memo, useMemo } from 'react';
import { Position } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsBox from '../../../SolyticsBox';
import SolyticsTypography from '../../../SolyticsTypography';
import NodeHandles from '../shared/NodeHandles';
import NodeTooltip from '../shared/NodeTooltip';
import NodeMenu from '../shared/NodeMenu';
import { NODE_COLOR_SCHEMES } from '../constants';
import { resolveNodeColors } from '../shared/themeColorUtils';

/**
 * CardShape — the pure visual shape used by CardNode.
 * Exported for consumers who need just the card rectangle without
 * ReactFlow internals (handles, menus, etc.).
 */
export const CardShape = memo(({ label, colors }) => {
  return (
    <SolyticsBox
      display="flex"
      alignItems="center"
      borderRadius="6px"
      bgcolor={colors.bg}
      maxWidth={200}
      minWidth={80}
      overflow="hidden"
      px={2}
      py={1}
      sx={{
        border: `1.5px solid ${colors.border}`,
        boxShadow: '0px 1px 2px rgba(0,0,0,0.06)',
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <SolyticsTypography color={colors.text} noWrap>
        {label}
      </SolyticsTypography>
    </SolyticsBox>
  );
});
CardShape.displayName = 'CardShape';

const CardNode = memo(({ id, data, parentId }) => {
  const theme = useTheme();

  const colors = useMemo(() => {
    return resolveNodeColors(theme, data, NODE_COLOR_SCHEMES);
  }, [data, theme]);

  const defaultHandles = useMemo(
    () => [
      { type: 'target', position: Position.Left, id: 'target' },
      { type: 'source', position: Position.Right, id: 'source' },
    ],
    []
  );

  return (
    <NodeMenu menuItems={data?.menuItems} nodeId={id} parentId={parentId}>
      <NodeTooltip tooltip={data?.tooltip}>
        <SolyticsBox display="inline-flex">
          <CardShape label={data?.label} colors={colors} />
        </SolyticsBox>
      </NodeTooltip>

      <NodeHandles handles={data?.handles} fallbackHandles={defaultHandles} />
    </NodeMenu>
  );
});
CardNode.displayName = 'CardNode';

export default CardNode;
